/**
 * The Asset Advisor: runs (list, start, read while it works, revise), the seller's notes, the client brief
 * confirmation, the brief PDFs, deleting a client's runs, and the unmatched demand report (archiving and restoring
 * its entries).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError, api } from '../client';
import type {
  AdvisorRun,
  AdvisorRunIn,
  AdvisorRunList,
  AdvisorRunListParams,
  AdvisorRunPatch,
  BriefKind,
  DemandArchiveReason,
  UnmatchedDemandItem,
  UnmatchedDemandList,
  UnmatchedDemandParams,
} from '../types/advisor';

/** A run still queued or running is asked for again every 3 s, and no longer once it ends. */
export const ADVISOR_POLL_MS = 3000;

export const advisorKeys = {
  all: ['advisor'] as const,
  runs: (params: AdvisorRunListParams) => ['advisor', 'runs', params] as const,
  run: (id: string) => ['advisor', 'run', id] as const,
  demand: (params: UnmatchedDemandParams) => ['advisor', 'demand', params] as const,
};

export function isWorking(status: string | undefined): boolean {
  return status === 'queued' || status === 'running';
}

export function advisorRunsQuery(params: AdvisorRunListParams): string {
  const search = new URLSearchParams({ scope: params.scope, page: String(params.page), page_size: String(params.page_size) });
  if (params.status) search.set('status', params.status);
  if (params.q.trim()) search.set('q', params.q.trim());
  return search.toString();
}

export function useAdvisorRuns(params: AdvisorRunListParams) {
  return useQuery({
    queryKey: advisorKeys.runs(params),
    queryFn: () => api<AdvisorRunList>(`/api/advisor/runs?${advisorRunsQuery(params)}`),
    placeholderData: (previous) => previous,
    refetchInterval: (query) => (query.state.data?.runs.some((r) => isWorking(r.status)) ? ADVISOR_POLL_MS : false),
  });
}

export function useAdvisorRun(id: string) {
  return useQuery({
    queryKey: advisorKeys.run(id),
    queryFn: () => api<AdvisorRun>(`/api/advisor/runs/${id}`),
    refetchInterval: (query) => (isWorking(query.state.data?.status) ? ADVISOR_POLL_MS : false),
  });
}

function useRefreshAdvisor() {
  const client = useQueryClient();
  return (run?: AdvisorRun) => {
    if (run) client.setQueryData(advisorKeys.run(run.id), run);
    void client.invalidateQueries({ queryKey: ['advisor', 'runs'] });
  };
}

export function useStartAdvisorRun() {
  const refresh = useRefreshAdvisor();
  return useMutation({
    meta: { success: 'Run started' },
    mutationFn: (body: AdvisorRunIn) => api<AdvisorRun>('/api/advisor/runs', 'POST', body),
    onSuccess: refresh,
  });
}

export function useRerunAdvisorRun(id: string) {
  const refresh = useRefreshAdvisor();
  return useMutation({
    meta: { success: 'Refined run started' },
    mutationFn: (body: Partial<AdvisorRunIn>) => api<AdvisorRun>(`/api/advisor/runs/${id}/rerun`, 'POST', body),
    onSuccess: refresh,
  });
}

export function useUpdateAdvisorRun(id: string) {
  const refresh = useRefreshAdvisor();
  return useMutation({
    meta: { success: 'Saved' },
    mutationFn: (body: AdvisorRunPatch) => api<AdvisorRun>(`/api/advisor/runs/${id}`, 'PATCH', body),
    onSuccess: refresh,
  });
}

/**
 * Confirms the brief the seller previewed, named by its `client_brief_hash`. When the brief changed since (409), the
 * run is read again so the dialog shows the brief as it reads now.
 */
export function useConfirmClientBrief(id: string) {
  const refresh = useRefreshAdvisor();
  const client = useQueryClient();
  return useMutation({
    meta: { success: 'Client brief confirmed' },
    mutationFn: (briefHash: string) =>
      api<AdvisorRun>(`/api/advisor/runs/${id}/client-brief/confirm`, 'POST', { brief_hash: briefHash }),
    onSuccess: refresh,
    onError: (error) => {
      if (error instanceof ApiError && error.status === 409) void client.invalidateQueries({ queryKey: advisorKeys.run(id) });
    },
  });
}

export function useDeleteClientRuns() {
  const client = useQueryClient();
  return useMutation({
    meta: { success: (answer: { deleted: number }) => `${answer.deleted} ${answer.deleted === 1 ? 'run' : 'runs'} deleted` },
    mutationFn: (name: string) => api<{ deleted: number }>('/api/advisor/clients', 'DELETE', { client: name }),
    onSuccess: () => void client.invalidateQueries({ queryKey: advisorKeys.all }),
  });
}

export function briefUrl(id: string, kind: BriefKind): string {
  return `/api/advisor/runs/${id}/brief.pdf?kind=${kind}`;
}

async function refusal(response: Response): Promise<ApiError> {
  if (response.status === 401) window.dispatchEvent(new CustomEvent('wynxx:unauthorized'));
  if (response.status === 429) return new ApiError(429, 'Too many exports; try again in a minute.');
  const payload = (await response.json().catch(() => null)) as { detail?: unknown } | null;
  return new ApiError(response.status, typeof payload?.detail === 'string' ? payload.detail : `Request failed (${response.status}).`);
}

/** The PDF in a new tab: opened before the request (a popup opened after an await is blocked), closed on a refusal. */
async function openBrief(path: string): Promise<void> {
  const tab = window.open('', '_blank');
  try {
    const response = await fetch(path, {
      credentials: 'same-origin',
      headers: { Accept: 'application/pdf', 'X-Requested-With': 'wynxx-discover' },
    });
    if (!response.ok) throw await refusal(response);
    const url = URL.createObjectURL(await response.blob());
    if (tab) {
      tab.opener = null;
      tab.location.href = url;
    } else {
      window.open(url, '_blank', 'noopener');
    }
    window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch (error) {
    tab?.close();
    throw error;
  }
}

export function useBriefPdf(id: string) {
  return useMutation({
    meta: { failure: 'Could not export the brief' },
    mutationFn: (kind: BriefKind) => openBrief(briefUrl(id, kind)),
  });
}

export function demandQuery(params: UnmatchedDemandParams): string {
  const search = new URLSearchParams({ status: params.status, page: String(params.page), page_size: String(params.page_size) });
  if (params.country) search.set('country', params.country);
  if (params.industry.trim()) search.set('industry', params.industry.trim());
  return search.toString();
}

export function useUnmatchedDemand(params: UnmatchedDemandParams) {
  return useQuery({
    queryKey: advisorKeys.demand(params),
    queryFn: () => api<UnmatchedDemandList>(`/api/portfolio/unmatched-demand?${demandQuery(params)}`),
    placeholderData: (previous) => previous,
  });
}

/**
 * Archiving or restoring moves the entry between the Open and Archived views, so every demand page is read again,
 * also after a refusal (409, 404): someone else acted on it first and the list is out of date.
 */
function useRefreshDemand() {
  const client = useQueryClient();
  return () => void client.invalidateQueries({ queryKey: ['advisor', 'demand'] });
}

export function useArchiveDemand() {
  const refresh = useRefreshDemand();
  return useMutation({
    meta: { success: 'Demand archived', failure: 'Could not archive' },
    mutationFn: ({ id, reason }: { id: string; reason: DemandArchiveReason }) =>
      api<UnmatchedDemandItem>(`/api/portfolio/unmatched-demand/${id}/archive`, 'POST', { reason }),
    onSettled: refresh,
  });
}

export function useRestoreDemand() {
  const refresh = useRefreshDemand();
  return useMutation({
    meta: { success: 'Demand restored', failure: 'Could not restore' },
    mutationFn: (id: string) => api<UnmatchedDemandItem>(`/api/portfolio/unmatched-demand/${id}/restore`, 'POST'),
    onSettled: refresh,
  });
}
